/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useEffect, useRef } from "react";
import { When } from "react-if";
import { Sidebar, Button, Input } from "@nordcloud/gnui";
import { LoaderWrap, MultiSelectSubmitContainer } from "~/components";
import { useDisclosure } from "~/hooks";
import { isNotEmpty } from "~/tools";
import { AppEnvTable } from "./AppEnvTable";
import { SelectorProps, Option, SidebarProps, SELECTOR_TYPE } from "./types";
import {
  filterApps,
  generateSelectedApps,
  generateSelectedEnvs,
  getButtonLabel,
  getInputLabelOnlyEnvironments,
  getInputLabel,
} from "./utils";

export function AppEnvSelector({
  onChange,
  onClear,
  applications,
  selectedApplications,
  selectedEnvironments,
  loading,
  selectionType = SELECTOR_TYPE.switch,
  onlyEnvironments = false,
}: SelectorProps) {
  const [selectedApps, setSelectedApps] = useState<Option[]>(
    generateSelectedApps(selectedApplications, applications)
  );
  const [selectedEnvs, setSelectedEnvs] = useState<Option[]>(
    generateSelectedEnvs(selectedEnvironments, applications)
  );

  const { isOpen, close, open } = useDisclosure();

  useEffect(() => {
    setSelectedApps(generateSelectedApps(selectedApplications, applications));
    setSelectedEnvs(generateSelectedEnvs(selectedEnvironments, applications));
  }, [isOpen]);

  const onClearHandler = () => {
    setSelectedApps([]);
    setSelectedEnvs([]);
    onClear?.();
  };

  const innerValue = onlyEnvironments
    ? getInputLabelOnlyEnvironments(
        selectedApps,
        applications,
        selectedEnvs.length
      )
    : getInputLabel(selectedApps, selectedEnvs);

  return (
    <>
      <Input
        readOnly
        type="text"
        title="Select applications/environments"
        placeholder="Select Applications and Environments"
        value={innerValue}
        icon="sidebar"
        showClearButton={isNotEmpty([...selectedApps, ...selectedEnvs])}
        onClick={open}
        onClear={onClearHandler}
      />
      <AppEnvSidebar
        isOpen={isOpen}
        applications={applications}
        selectedEnvs={selectedEnvs}
        setSelectedEnvs={setSelectedEnvs}
        selectedApps={selectedApps}
        setSelectedApps={setSelectedApps}
        loading={loading}
        selectionType={selectionType}
        onlyEnvironments={onlyEnvironments}
        onChange={onChange}
        onClose={close}
      />
    </>
  );
}

function AppEnvSidebar({
  isOpen,
  onChange,
  onClose,
  applications,
  selectedEnvs,
  setSelectedEnvs,
  selectedApps,
  setSelectedApps,
  loading,
  selectionType = SELECTOR_TYPE.switch,
  onlyEnvironments,
  disabled = false,
}: SidebarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<string>("");

  useEffect(() => {
    !isOpen && setFilter("");

    const inputRefCurrent = inputRef?.current;
    if (isOpen && inputRefCurrent) {
      inputRefCurrent.focus();
    }
  }, [setFilter, isOpen]);

  const onSubmitHandle = () => {
    onClose();
    onChange(
      selectedApps.map((app) => app.nid),
      selectedEnvs.map((env) => env.nid)
    );
  };

  return (
    <Sidebar
      isOpen={isOpen}
      title="Select applications and environments"
      footer={
        <When condition={!loading && !disabled}>
          <MultiSelectSubmitContainer>
            <Button onClick={onSubmitHandle}>
              {onlyEnvironments
                ? getInputLabelOnlyEnvironments(
                    selectedApps,
                    applications,
                    selectedEnvs.length
                  )
                : getButtonLabel(selectedApps.length, selectedEnvs.length)}
            </Button>
          </MultiSelectSubmitContainer>
        </When>
      }
      onClick={onClose}
    >
      <LoaderWrap inContent loading={loading}>
        <Input
          ref={inputRef}
          small
          type="filter"
          value={filter}
          name="filter"
          placeholder="Filter"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFilter(event.target.value)
          }
        />
        <AppEnvTable
          filteredApps={filterApps(applications, filter)}
          selectedEnvs={selectedEnvs}
          setSelectedEnvs={setSelectedEnvs}
          selectedApps={selectedApps}
          setSelectedApps={setSelectedApps}
          selectionType={selectionType}
          disabled={disabled}
        />
      </LoaderWrap>
    </Sidebar>
  );
}
